import { ServiceFormsType, ServiceMenuOptionTime } from "@/apis/clients";
import { ITextToCopyProps } from "@/pages/lp/[uuid]/ticket/[ticket_id]";
import { AspectRatioType } from "@/states/page-editor/template-parts/common";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { defaultImgRectangleSrc } from "@/utils/convert-page-json";
import { DateFormat, format } from "@/utils/date";

/** 共通関数を定義します. */
export {};

/**
 * start から end までの整数の配列を返します。
 */
export function range(start: number, end: number) {
  const result: number[] = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
}

/**
 * nullまたはundefinedではないことをアサートします
 */
export function assertIsDefined<T>(val: T): asserts val is NonNullable<T> {
  if (val === undefined || val === null) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Expected 'val' to be defined, but received ${val}`);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDataTestId = (props: any): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
  return props["data-testid"] ?? undefined;
};

// オブジェクトの配列をフィールド名でgroupBy
export const groupBy = <T extends { [key: string]: any }>(
  objects: T[],
  key: keyof T,
): { [key: string]: T[] } =>
  objects.reduce((map, obj) => {
    map[obj[key]] = map[obj[key]] || [];
    map[obj[key]].push(obj);
    return map;
  }, {} as { [key: string]: T[] });

/**
 * ユニークなidを生成する.
 * @param myStrong
 * @returns
 */
export const getUniqueStr = (myStrong?: number): string => {
  let strong = 1000;
  if (myStrong) strong = myStrong;
  return (
    new Date().getTime().toString(16) +
    Math.floor(strong * Math.random()).toString(16)
  );
};

/**
 * 読み込んだ画像の情報(width,height,src)
 */
export interface ImageProperty {
  width: number;
  height: number;
  src: string;
}
/**
 * 画像ファイルの情報(width,height,src)を返す
 */
export const getImageProperty = async (
  file: string | Blob | MediaSource,
  revoke?: boolean,
): Promise<ImageProperty> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const prop: ImageProperty = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        src: img.src,
      };
      // 引数にてtrueを指定した場合、生成した画像を破棄する
      if (revoke) {
        URL.revokeObjectURL(img.src);
      }
      resolve(prop);
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = typeof file === "string" ? file : URL.createObjectURL(file);
  });
};

/**
 * 最大公約数を求める
 */
const gcd = (a: number, b: number): number => {
  if (b == 0) return a;
  return gcd(b, a % b);
};

/**
 * アスペクト比を求める
 */
export const aspectRatio = (w: number, h: number): AspectRatioType => {
  const g = gcd(w, h);
  return [w / g, h / g];
};

/**
 * 値が空かどうか判定する
 */
export const isEmptyValue = (
  val: string | number | Array<unknown> | object | undefined,
): boolean => {
  if (typeof val === "undefined" || val === undefined || val === null) {
    return true;
  }
  // 文字列に何も入ってない場合(空白文字は判定外)
  if (typeof val === "string" && val === "") {
    return true;
  }
  // 配列に何も入ってない場合
  if (Array.isArray(val) && val.length === 0) {
    return true;
  }
  if (typeof val === "object" && Object.keys(val).length === 0) {
    return true;
  }
  return false;
};

/**
 * 画像未設定の場合、デフォルト画像を表示
 */
export const imgSrc = (url: string | undefined, defaultImg?: string) => {
  return url && !isEmptyValue(url) ? url : defaultImg || defaultImgRectangleSrc;
};

/**
 * 網羅性チェック
 */
export function assertNever(x: never): never {
  throw new Error("Unexpected value. Should have been never.");
}

/**
 * navigator.clipboardがNULLの場合、指定したテキストをクリップボードにコピーします
 */
function unsecuredCopyToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand("copy");
  } catch (err) {
    return false;
  }
  document.body.removeChild(textArea);
  return true;
}

/**
 * 指定したテキストをクリップボードにコピーします
 */
export async function clipboardcopy(text: string): Promise<boolean> {
  return new Promise((resolve, error) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(
        function () {
          resolve(true);
        },
        function () {
          error();
        },
      );
    } else {
      if (unsecuredCopyToClipboard(text)) {
        resolve(true);
      } else {
        error();
      }
    }
  });
}

export function scrollToElement(
  element: "top" | HTMLElement | Element | null,
  block?: ScrollLogicalPosition,
) {
  if (element === "top") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: block || "start",
      inline: "nearest",
    });
  }
}

export function isCorrectUrl(url: string): boolean {
  if (!url) return false;

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return true;
  }

  return false;
}

export function getCorrectUrl(url: string): string {
  if (!url) return "";

  if (!isCorrectUrl(url)) {
    url = "https://" + url;
  }

  return url;
}

/**
 * カレンダータイプの『オプションの時間』
 */
export const CalendarServiceMenuOptionTime: {
  value: ServiceMenuOptionTime;
  text: string;
}[] = [
  { value: ServiceMenuOptionTime.MINUTES_0, text: "0" },
  { value: ServiceMenuOptionTime.MINUTES_15, text: "15" },
  { value: ServiceMenuOptionTime.MINUTES_20, text: "20" },
  { value: ServiceMenuOptionTime.MINUTES_25, text: "25" },
  { value: ServiceMenuOptionTime.MINUTES_30, text: "30" },
  { value: ServiceMenuOptionTime.MINUTES_35, text: "35" },
  { value: ServiceMenuOptionTime.MINUTES_40, text: "40" },
  { value: ServiceMenuOptionTime.MINUTES_45, text: "45" },
  { value: ServiceMenuOptionTime.MINUTES_50, text: "50" },
  { value: ServiceMenuOptionTime.MINUTES_55, text: "55" },
  { value: ServiceMenuOptionTime.MINUTES_60, text: "60" },
  { value: ServiceMenuOptionTime.MINUTES_70, text: "70" },
  { value: ServiceMenuOptionTime.MINUTES_75, text: "75" },
  { value: ServiceMenuOptionTime.MINUTES_80, text: "80" },
  { value: ServiceMenuOptionTime.MINUTES_90, text: "90" },
  { value: ServiceMenuOptionTime.MINUTES_100, text: "100" },
  { value: ServiceMenuOptionTime.MINUTES_120, text: "120" },
  { value: ServiceMenuOptionTime.MINUTES_150, text: "150" },
  { value: ServiceMenuOptionTime.MINUTES_180, text: "180" },
  { value: ServiceMenuOptionTime.MINUTES_210, text: "210" },
  { value: ServiceMenuOptionTime.MINUTES_240, text: "240" },
  { value: ServiceMenuOptionTime.MINUTES_300, text: "300" },
];
/**
 * メニューの所要時間を取得
 */
export function getCalendarTimeRequired(time_required: number) {
  const opt = CalendarServiceMenuOptionTime.find(
    (option) => option.value === time_required,
  );
  return opt?.text;
}

export const checkIsCustomField = (type: ServiceFormsType): boolean => {
  return (
    type === ServiceFormsType.SINGLE_LINE_DESCRIPTION ||
    type === ServiceFormsType.MULTI_LINE_DESCRIPTION ||
    type === ServiceFormsType.OPTIONS ||
    type === ServiceFormsType.UPLOAD
  );
};

export const checkIsCustomDescription = (type: ServiceFormsType): boolean => {
  return (
    type === ServiceFormsType.TEL ||
    type === ServiceFormsType.MAIL_ADDRESS ||
    type === ServiceFormsType.MAIL_DELIVERY ||
    type === ServiceFormsType.ZIP
  );
};

export const getDefaultIframeBlock = (
  type: "event-seminar" | "form" | "calendar" | "subscription" | string,
) => {
  switch (type) {
    case "event-seminar":
      return {
        blocks: [
          {
            key: "dgtod",
            data: {
              TEMPLATE: {
                id: "dgtod",
                type: "menu.eventSeminar",
                title: {
                  content: {
                    blocks: [
                      {
                        key: "ef3ks",
                        data: [],
                        text: "お客様情報を入力してください",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: [],
                  },
                  textBaseStyle: {
                    fontSize: "2rem",
                  },
                },
                buttonContent: {
                  id: "dgtod",
                  content: {
                    blocks: [
                      {
                        key: "1urvh",
                        data: [],
                        text: "申し込みへ進む",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: {},
                  },
                  defaultColor: "#45BEAB",
                  cssProperties: {
                    backgroundColor: "#FF6B0F",
                    borderColor: "#FF6B0F",
                    borderRadius: "4px",
                    height: "56px",
                    maxHeight: "56px",
                    minHeight: "56px",
                    color: "#FFFFFF",
                  },
                },
                frameCssProperties: {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#FFFFFF",
                  borderRadius: "8px",
                  marginTop: "64px",
                },
                bgCssProperties: {
                  backgroundColor: "initial",
                },
              },
            },
            text: "",
            type: "atomic",
            depth: 0,
            entityRanges: [],
            inlineStyleRanges: [],
          },
        ],
        entityMap: [
          {
            data: [],
            type: "TEMPLATE",
            mutability: "IMMUTABLE",
          },
        ],
      };
    case "form":
      return {
        blocks: [
          {
            key: "ecf8h",
            data: {
              TEMPLATE: {
                id: "ecf8h",
                type: "menu.form",
                title: {
                  content: {
                    blocks: [
                      {
                        key: "ef3ks",
                        data: [],
                        text: "お客様情報を入力してください",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: [],
                  },
                  textBaseStyle: {
                    fontSize: "2rem",
                  },
                },
                buttonContent: {
                  id: "ecf8h",
                  content: {
                    blocks: [
                      {
                        key: "1urvh",
                        data: [],
                        text: "申し込みへ進む",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: {},
                  },
                  defaultColor: "#45BEAB",
                  cssProperties: {
                    backgroundColor: "#FF6B0F",
                    borderColor: "#FF6B0F",
                    borderRadius: "4px",
                    height: "56px",
                    maxHeight: "56px",
                    minHeight: "56px",
                    color: "#FFFFFF",
                  },
                },
                frameCssProperties: {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#FFFFFF",
                  borderRadius: "8px",
                  marginTop: "64px",
                },
                bgCssProperties: {
                  backgroundColor: "initial",
                },
              },
            },
            text: "",
            type: "atomic",
            depth: 0,
            entityRanges: [],
            inlineStyleRanges: [],
          },
        ],
        entityMap: [
          {
            data: [],
            type: "TEMPLATE",
            mutability: "IMMUTABLE",
          },
        ],
      };
    case "calendar":
      return {
        blocks: [
          {
            key: "ecf8g",
            data: {
              TEMPLATE: {
                id: "ecf8g",
                type: "menu.calendar",
                frameCssProperties: {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#FFFFFF",
                  borderRadius: "8px",
                },
                bgCssProperties: {
                  backgroundColor: "initial",
                },
              },
            },
            text: "",
            type: "atomic",
            depth: 0,
            entityRanges: [],
            inlineStyleRanges: [],
          },
        ],
        entityMap: [
          {
            data: [],
            type: "TEMPLATE",
            mutability: "IMMUTABLE",
          },
        ],
      };
    case "subscription":
      return {
        blocks: [
          {
            key: "subsc",
            data: {
              TEMPLATE: {
                id: "subsc",
                type: "menu.subscription",
                title: {
                  content: {
                    blocks: [
                      {
                        key: "ubscr",
                        data: [],
                        text: "お客様情報を入力してください",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: [],
                  },
                  textBaseStyle: {
                    fontSize: "2rem",
                  },
                },
                buttonContent: {
                  id: "subsc",
                  content: {
                    blocks: [
                      {
                        key: "bscri",
                        data: [],
                        text: "申し込みへ進む",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: {},
                  },
                  defaultColor: "#45BEAB",
                  cssProperties: {
                    backgroundColor: "#FF6B0F",
                    borderColor: "#FF6B0F",
                    borderRadius: "4px",
                    height: "56px",
                    maxHeight: "56px",
                    minHeight: "56px",
                    color: "#FFFFFF",
                  },
                },
                frameCssProperties: {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#FFFFFF",
                  borderRadius: "8px",
                  marginTop: "64px",
                },
                bgCssProperties: {
                  backgroundColor: "initial",
                },
              },
            },
            text: "",
            type: "atomic",
            depth: 0,
            entityRanges: [],
            inlineStyleRanges: [],
          },
        ],
        entityMap: [
          {
            data: [],
            type: "TEMPLATE",
            mutability: "IMMUTABLE",
          },
        ],
      };
    default:
      return {
        blocks: [
          {
            key: "ecf8h2",
            data: {
              TEMPLATE: {
                id: "ecf8h2",
                type: "menu.other",
                buttonContent: {
                  id: "ecf8h2",
                  content: {
                    blocks: [
                      {
                        key: "1urvh",
                        data: [],
                        text: "申し込みへ進む",
                        type: "unstyled",
                        depth: 0,
                        entityRanges: [],
                        inlineStyleRanges: [],
                      },
                    ],
                    entityMap: {},
                  },
                  defaultColor: "#45BEAB",
                  cssProperties: {
                    backgroundColor: "#AB9548",
                    borderColor: "#AB9548",
                    borderRadius: "28px",
                    height: "56px",
                    maxHeight: "56px",
                    minHeight: "56px",
                    color: "#FFFFFF",
                  },
                },
                frameCssProperties: {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#DFDFE2",
                  borderRadius: "16px",
                },
                bgCssProperties: {
                  backgroundColor: "initial",
                },
              },
            },
            text: "",
            type: "atomic",
            depth: 0,
            entityRanges: [],
            inlineStyleRanges: [],
          },
        ],
        entityMap: [
          {
            data: [],
            type: "TEMPLATE",
            mutability: "IMMUTABLE",
          },
        ],
      };
  }
};

export const getDefaultOgpImage = (parts: TemplatePart[]) => {
  if (!parts || !parts.length) return "";

  for (const part of parts) {
    switch (part.type) {
      case "slider.image": {
        const slideImage = part.slides?.find((value) => !!value.image);
        if (slideImage) return slideImage.image;
        break;
      }
      case "style_lp.instagram": {
        const slideImage = part.slider?.slides?.find((value) => !!value.image);
        if (slideImage) return slideImage.image;
        break;
      }
      case "image":
        if (part.src) return part.src;
        break;
      case "image.button":
        if (part.src) return part.src;
        break;
      default:
        break;
    }
  }

  return "";
};

export const isIframePage = (uuid: string) => {
  return !isNaN(+uuid);
};

export const downloadFileFromUrl = (url: string, fileName: string) => {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      // Create a temporary URL object
      const url = URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      // Programmatically click the link to start the download
      link.click();

      // Clean up the temporary URL object
      URL.revokeObjectURL(url);
    });
};

export const QrCodeTextToCopy = (
  {order, ticket, customer, lpId}: ITextToCopyProps
) => {
  const day = format(new Date(order.event_date), DateFormat.SLASH_JP);
  const period = `${format(new Date(0, 0, 0, order?.start_hour ?? 0, order?.start_minute ?? 0), DateFormat.HOURS_MINUTES)}〜${format(new Date(0, 0, 0, order?.end_hour ?? 0, order?.end_minute ?? 0), DateFormat.HOURS_MINUTES)}`;
  return (
`${order?.menu_name || ''}
${day}${period}
申込者：${customer?.last_name || ''} ${customer?.first_name || ''}
チケット番号：${ticket.name}
チケットURL：${process.env.NEXT_PUBLIC_BASE_URL as string}/lp/${lpId ?? ''}/ticket/${ticket.id}
`
  );
};
/**
 * Fileをbase64に変換する
 * @param file
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = (error) => reject(error);
  });
}

export function getFileName(file: File): string {
  const now = new Date().getTime();
  const fileType = file.type;
  const ext =
    file.name.split(".").pop() ??
    fileType.substring(fileType.lastIndexOf("/") + 1, fileType.length);
  return `${now}.${ext}`;
}
